import { useEffect, useRef, useState } from 'react';
import { supabase } from '@/lib/supabase';
export function useAuth() {
    const [state, setState] = useState({ user: null, role: null, loading: true });
    // Track the current user id so stale async responses are ignored
    const currentUserId = useRef(undefined);
    async function applyUser(user) {
        currentUserId.current = user?.id ?? null;
        if (!user) {
            setState({ user: null, role: null, loading: false });
            return;
        }
        const userId = user.id;
        try {
            const { data: profile } = await supabase
                .from('profiles')
                .select('role')
                .eq('id', userId)
                .maybeSingle();
            if (currentUserId.current === userId) {
                setState({ user, role: profile?.role ?? null, loading: false });
            }
        }
        catch {
            if (currentUserId.current === userId) {
                setState({ user, role: null, loading: false });
            }
        }
    }
    useEffect(() => {
        // getSession() resolves the initial session without holding the auth lock,
        // so nested supabase calls (profile fetch) are safe here.
        supabase.auth.getSession().then(({ data: { session } }) => {
            applyUser(session?.user ?? null);
        });
        // onAuthStateChange handles subsequent changes (token refresh, sign out).
        // We skip INITIAL_SESSION because getSession() above already handles it —
        // having both would cause two concurrent profile fetches and lock contention.
        // For all other events, defer via setTimeout so the callback's internal lock
        // is released before we issue another supabase call.
        const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
            if (event === 'INITIAL_SESSION')
                return;
            const user = session?.user ?? null;
            setTimeout(() => applyUser(user), 0);
        });
        return () => subscription.unsubscribe();
    }, []);
    return state;
}
